import { useEffect, useState } from "react";
import IconButtonComponent from "../UI/buttons/IconButton";
import { ReactComponent as BlackArrowRight } from "../../assets/icons/chevron_right_black.svg";
import dateUtil from "../../utils/dateUtil";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { clearKpiData } from "../../store/KPICollection";
import { openDragAndDropSessionWarningModal } from "../../store/dragAndDrop";
import RequestCancellationService from "../../service/requestCancellationService";
import labels from "../../utils/labels";
import UiDatePicker from "../UI/input/DatePicker";

const DatePicker = ({
  min,
  max,
  showArrows,
  setSelectedDate,
  defaultDate,
  cleanUpFn,
}: {
  min?: string;
  max?: string;
  showArrows?: boolean;
  setSelectedDate: (date: string) => void;
  defaultDate?: string;
  cleanUpFn?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { selectedGanttChartTab } = useAppSelector(
    (state) => state.ganttChartSlice,
  );

  const sanitizeDateInput = (value?: string | Date) => {
    if (!value) {
      return dateUtil.getCalendarValueString(new Date());
    }

    if (value instanceof Date) {
      return dateUtil.getCalendarValueString(value);
    }

    const trimmedValue = value.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return trimmedValue;
    }

    const parsedDate = new Date(trimmedValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return dateUtil.getCalendarValueString(new Date());
    }

    return dateUtil.getCalendarValueString(parsedDate);
  };

  // Format: YYYY-MM-DD
  const [inputDateValue, setInputDateValue] = useState<string>(
    sanitizeDateInput(defaultDate),
  );

  useEffect(() => {
    const nextInputValue = sanitizeDateInput(defaultDate);

    setInputDateValue((prev: string) => {
      if (prev === nextInputValue) {
        return prev;
      }
      return nextInputValue;
    });
  }, [defaultDate]);

  useEffect(() => {
    setSelectedDate(new Date(inputDateValue).toISOString());
  }, [inputDateValue]);

  const moveXDay = (numberOfMoves: number) => {
    cleanUpFn?.();
    const dayBeforeCurrentlySelectedDay = new Date(inputDateValue);
    dayBeforeCurrentlySelectedDay.setDate(
      dayBeforeCurrentlySelectedDay.getDate() + numberOfMoves,
    );
    setInputDateValue(
      dateUtil.getCalendarValueString(dayBeforeCurrentlySelectedDay),
    );
  };

  const handleArrowClick = (count: number) => {
    if (selectedGanttChartTab === "Custom")
      dispatch(openDragAndDropSessionWarningModal());
    else {
      dispatch(clearKpiData());
      moveXDay(count);
      cancelRunningAPICalls();
    }
  };

  const cancelRunningAPICalls = () => {
    const requestService = RequestCancellationService.getInstance();
    requestService.cancelAllRequests();
  };

  return (
    <div className="flex items-center gap-[var(--spacing-xs)]">
      {showArrows && (
        <IconButtonComponent
          ariaLabel={labels.moveDayBackward}
          key="moveBackwardIcon"
          id="moveBackwardIcon"
          icon={<BlackArrowRight style={{ transform: "rotate(180deg)" }} />}
          disable={inputDateValue === min}
          onClick={() => handleArrowClick(-1)}
          classes="border border-[var(--col-border-primary)] !rounded-full bg-white"
        />
      )}
      <UiDatePicker
        id="header-date-picker"
        classes="min-w-[9rem]"
        value={new Date(inputDateValue + "T12:00:00").toISOString()}
        onDateChange={(isoDate: string) => {
          cleanUpFn?.();
          setInputDateValue(sanitizeDateInput(isoDate));
          cancelRunningAPICalls();
        }}
        min={min}
        max={max}
        onClickOverride={
          selectedGanttChartTab === "Custom"
            ? () => dispatch(openDragAndDropSessionWarningModal())
            : undefined
        }
      />
      {showArrows && (
        <IconButtonComponent
          ariaLabel={labels.moveDayForward}
          key="moveForwardIcon"
          id="moveForwardIcon"
          icon={<BlackArrowRight />}
          disable={inputDateValue === max}
          onClick={() => handleArrowClick(1)}
          classes="border border-[var(--col-border-primary)] !rounded-full bg-white"
        />
      )}
    </div>
  );
};
export default DatePicker;
